// useValidation.ts
import type { MpfFormFieldConfig } from '../components/MpfForm/MpfForm.props';

export interface ValidationResult {
  errors: Record<string, string>;
  valid: boolean;
}

function validateEmail(value: string): boolean {
  if (!value) return true; // required handled separately
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
}

function validatePhone(value: string): boolean {
  if (!value) return true;
  const phoneRegex = /^[+\d\s().-]{7,20}$/;
  return phoneRegex.test(value);
}

function validatePostcode(value: string): boolean {
  if (!value) return true;
  const postcodeRegex = /^([A-Z]{1,2}\d[A-Z\d]?\s*\d[ABD-HJLNP-UW-Z]{2})$/i;
  return postcodeRegex.test(value.trim());
}

export function validateTextField(field: MpfFormFieldConfig, value: string): string {
  const trimmed = value?.trim?.() ?? '';

  if (field.required && !trimmed) {
    return 'This field is required.';
  }

  if (field.type === 'email' && trimmed && !validateEmail(trimmed)) {
    return 'Please enter a valid email address.';
  }

  if (field.type === 'phone' && trimmed && !validatePhone(trimmed)) {
    return 'Please enter a valid phone number.';
  }

  if (field.type === 'postcode' && trimmed && !validatePostcode(trimmed)) {
    return 'Please enter a valid postcode.';
  }

  return '';
}

export function validateDateField(field: MpfFormFieldConfig, value: string): string {
  const dateStr = value?.trim?.() ?? '';

  if (!dateStr) {
    return field.required ? 'Please enter a valid date.' : '';
  }

  const parts = dateStr.split('-');
  if (parts.length !== 3) {
    return 'Enter a real date in the format YYYY-MM-DD.';
  }

  const [yyyy, mm, dd] = parts;
  const dateObj = new Date(`${yyyy}-${mm}-${dd}`);

  const validDate =
    dateObj instanceof Date &&
    !isNaN(dateObj.getTime()) &&
    dateObj.getUTCFullYear() === Number(yyyy) &&
    dateObj.getUTCMonth() + 1 === Number(mm) &&
    dateObj.getUTCDate() === Number(dd);

  if (!validDate) {
    return 'Enter a real date (for example, 1984-03-15).';
  }

  return '';
}

export function validateAll(
  fields: MpfFormFieldConfig[],
  values: Record<string, any>,
): ValidationResult {
  const errors: Record<string, string> = {};
  let valid = true;

  fields.forEach((field) => {
    const value = values[field.name];
    let error = '';

    // NB: Pluralised naming for groups of checkbox OR radio items
    // CHECKBOXES (array)
    if (field.type === 'checkboxes') {
      if (field.required && (!Array.isArray(value) || value.length === 0)) {
        error = 'Please select at least one option.';
      }
    } else if (field.type === 'radios') {
      if (field.required && (value === null || value === undefined || value === '')) {
        error = 'Please choose an option.';
      }
    } else if (field.type === 'date' || field.type === 'dateOfBirth') {
      error = validateDateField(field, String(value ?? ''));
    } else {
      error = validateTextField(field, String(value ?? ''));
    }

    errors[field.name] = error;
    if (error) valid = false;
  });

  return { errors, valid };
}

export function useValidation() {
  return {
    validateAll,
  };
}
